import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarCheck, Clock, BellRinging } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const TIPO_CLS = { visita: "bg-emerald-500", reunion: "bg-indigo-500", llamada: "bg-sky-500", cita: "bg-amber-500", vencimiento: "bg-red-500", inspeccion: "bg-orange-500", mantenimiento: "bg-orange-600", renovacion: "bg-purple-500", cobranza: "bg-teal-600", juridico: "bg-rose-600", otro: "bg-neutral-500" };
const SNOOZE_MS = 60 * 60 * 1000; // 1 hora

export default function DailyAgendaReminder() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef();

  const dayKey = (uid) => `agenda_reminder_${uid}_${iso(new Date())}`;

  useEffect(() => {
    if (!user) return;
    const uid = user.id || user.email;
    let active = true;

    const canShow = () => {
      const raw = localStorage.getItem(dayKey(uid));
      if (!raw) return true;
      if (raw === "dismissed") return false; // "No mostrar hoy"
      const snoozeUntil = Number(raw);
      return Number.isFinite(snoozeUntil) ? Date.now() >= snoozeUntil : false;
    };

    const fetchAndMaybeShow = () => {
      if (!canShow()) return;
      const today = iso(new Date());
      api
        .get("/agenda/events", { params: { desde: today, hasta: today } })
        .then((r) => {
          if (!active) return;
          const evs = (r.data || [])
            .filter((e) => e.estatus !== "cancelado")
            .sort((a, b) => (a.hora || "").localeCompare(b.hora || ""));
          if (evs.length) {
            setEvents(evs);
            setOpen(true);
          }
        })
        .catch(() => {});
    };

    fetchAndMaybeShow();
    timer.current = setInterval(() => {
      if (!open) fetchAndMaybeShow();
    }, 60000);
    return () => { active = false; clearInterval(timer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const snooze = () => {
    const uid = user.id || user.email;
    localStorage.setItem(dayKey(uid), String(Date.now() + SNOOZE_MS));
    setOpen(false);
  };
  const dismissToday = () => {
    const uid = user.id || user.email;
    localStorage.setItem(dayKey(uid), "dismissed");
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) snooze(); }}>
      <DialogContent className="rounded-sm max-w-md" data-testid="daily-agenda-reminder">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarCheck size={22} className="text-primary" weight="duotone" />
            Recordatorios de hoy
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground -mt-1">
          Tienes {events.length} evento(s) programado(s) para hoy.
        </p>
        <ul className="space-y-2 max-h-72 overflow-y-auto mt-1">
          {events.map((e) => (
            <li key={e.id} className="flex items-start gap-2.5 border border-border rounded-sm p-2.5">
              <span className={`w-2.5 h-2.5 rounded-full mt-1 shrink-0 ${TIPO_CLS[e.tipo] || TIPO_CLS.otro}`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{e.titulo}</div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
                  <Clock size={13} />
                  {e.hora ? `${e.hora}${e.hora_fin ? ` – ${e.hora_fin}` : ""}` : "Sin hora"}
                  <span className="capitalize">· {e.tipo}</span>
                  {e.cliente ? <span className="truncate">· {e.cliente}</span> : null}
                </div>
              </div>
            </li>
          ))}
        </ul>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="ghost" className="rounded-sm" data-testid="reminder-dismiss-today" onClick={dismissToday}>
            No mostrar hoy
          </Button>
          <Button variant="outline" className="rounded-sm gap-1.5" data-testid="reminder-snooze" onClick={snooze}>
            <BellRinging size={15} /> Recordarme en 1 hora
          </Button>
          <Button
            className="rounded-sm"
            data-testid="reminder-goto-agenda"
            onClick={() => { dismissToday(); navigate("/agenda"); }}
          >
            Ir a la Agenda
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
